// `/reset` (UX-6a) — the consume half of the password reset (spec 16 §3.4.3/4;
// contract: POST /v1/auth/password/reset). THE PATH IS THE EMAIL'S: the API's
// reset mail links `${appBaseUrl}/reset?token=…` (identity/reset.service.ts),
// so this screen answers that exact address — the file is named for what it
// does, the route for where the mail points.
//
// Three entry states, all designed rather than fallen into:
//   * no `?token` — the guidance state: this page only means something when
//     reached from the email; say so and point at /forgot-password.
//   * a token — the form. Two fields (new + repeat, matched client-side so a
//     typo cannot burn the SINGLE-USE token), then the POST. A 400 is one of
//     two contract codes: INVALID_OR_EXPIRED_TOKEN (generic by design — no
//     token oracle) or PASSWORD_POLICY.
//   * success — the response has already set a fresh session cookie (every
//     other session is revoked, spec §3.4.4). `step: "totp"` means the mailbox
//     proved control but not the second factor: the TOTP challenge renders
//     here, exactly as on the login screen, before Continue.
//
// FLOOR-PRINCIPAL NOTE, deliberate: unlike login, this flow does NOT call
// setFloorPrincipal/resumeAfterSignIn — the reset response carries no email
// and inventing one would defeat the outbox's owner check (floor-principal.ts).
// A floor worker who resets by email signs in once at /login afterwards to
// unfreeze a queued outbox; the office majority never notices.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { client } from "../lib/api";
import { openFloorReadCache } from "../areas/floor-pwa/lib/floor-read-cache";
import { resetProfileRead } from "../lib/profile-locale";
import { resetRefNames } from "../lib/ref-names";
import { resetRoleFeed } from "../lib/role-codes";
import { Banner } from "../components/toast";
import { Button } from "../components/button";
import { AdminResetPasswordScreen } from "../areas/admin/screens/reset-password";
import { isAdminHost } from "../lib/host";

/** Where Continue lands. Mirrors login.tsx's landingRoute in spirit, but the
 * simple form: the onboarding probe decides wizard-vs-home; role-specific
 * landings need the roles feed, which the fresh session serves at /home's own
 * shell anyway. */
async function landing(): Promise<"/onboarding" | "/home"> {
  try {
    const { data } = await client.GET("/v1/onboarding/session");
    if (data !== undefined && data.session.status !== "live") return "/onboarding";
  } catch {
    // fall through
  }
  return "/home";
}

/**
 * This path exists on BOTH kinds of host (D-587), like `/login`: a tenant host
 * serves the factory user's reset link (spec 16 §3.4), and `admin.<apex>` serves
 * the platform staff one (spec 15 §3.1 step 7), whose API answers on that host
 * alone. No hook runs above the branch, so each side keeps its own hook order.
 */
export function ResetPasswordPage() {
  if (isAdminHost(window.location.hostname)) return <AdminResetPasswordScreen />;
  return <TenantResetPasswordPage />;
}

function TenantResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Read ONCE, then scrub — the M15 accept-invite precedent: after first paint
  // the address bar carries no credential, so a screenshot, a shared screen or
  // a browser-history sync leaks nothing. A reload after scrubbing lands on
  // the no-token guidance, exactly like a mangled mail link.
  const [token] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("token");
    if (fromUrl !== null && fromUrl !== "") {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
    return fromUrl;
  });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [step, setStep] = useState<"form" | "totp" | "done">("form");
  const [code, setCode] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST("/v1/auth/password/reset", {
        body: { token: token ?? "", new_password: password },
      });
      if (error) {
        const code = (error as { code?: string }).code;
        throw new Error(
          code === "INVALID_OR_EXPIRED_TOKEN"
            ? "auth.resetInvalid"
            : code === "PASSWORD_POLICY"
              ? "auth.resetPolicy"
              : "auth.resetError",
        );
      }
      return data;
    },
    onSuccess: (data) => {
      setErrorKey(null);
      // The reset signs this person in; a profile read and the names read for
      // anyone else go, and the floor read cache a sign-out closed reopens
      // for this session (D-635).
      openFloorReadCache();
      resetProfileRead();
      resetRefNames(queryClient);
      resetRoleFeed(queryClient);
      setStep(data.step === "totp" ? "totp" : "done");
    },
    onError: (err: unknown) => {
      setErrorKey(err instanceof Error ? err.message : "auth.resetError");
    },
  });

  const verifyTotp = useMutation({
    mutationFn: async () => {
      const { error } = await client.POST("/v1/auth/totp/verify", { body: { code } });
      if (error) throw new Error("login.totpError");
      return undefined;
    },
    onSuccess: () => {
      setErrorKey(null);
      setStep("done");
    },
    onError: (err: unknown) => {
      setErrorKey(err instanceof Error ? err.message : "login.totpError");
    },
  });

  if (token === null || token === "") {
    return (
      <section className="auth-card" data-testid="reset-password">
        <h1 className="auth-title">{t("auth.resetTitle")}</h1>
        <Banner kind="warn" data-testid="reset-no-token">
          {t("auth.resetNoToken")}
        </Banner>
        <div className="auth-links">
          <Link to="/forgot-password" className="ds-lnk">
            {t("auth.requestNew")}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-card" data-testid="reset-password">
      <h1 className="auth-title">{t("auth.resetTitle")}</h1>
      {step === "form" ? <p className="auth-lead">{t("auth.resetLead")}</p> : null}

      {errorKey !== null ? (
        <Banner kind="bad" data-testid="reset-error">
          {t(errorKey)}
          {errorKey === "auth.resetInvalid" ? (
            <>
              {" "}
              <Link to="/forgot-password" className="ds-lnk">
                {t("auth.requestNew")}
              </Link>
            </>
          ) : null}
        </Banner>
      ) : null}
      {mismatch ? (
        <Banner kind="bad" data-testid="reset-mismatch">
          {t("auth.resetMismatch")}
        </Banner>
      ) : null}

      {step === "form" ? (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            // A mismatch must never reach the server: the token is single-use,
            // and burning it on a typo strands the user back at the email.
            if (password !== confirm) {
              setMismatch(true);
              return;
            }
            setMismatch(false);
            reset.mutate();
          }}
        >
          <div className="ds-field">
            <label htmlFor="new-password">{t("auth.resetPassword")}</label>
            <input
              id="new-password"
              name="new-password"
              className="ds-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              required
            />
          </div>
          <div className="ds-field">
            <label htmlFor="confirm-password">{t("auth.resetConfirm")}</label>
            <input
              id="confirm-password"
              name="confirm-password"
              className="ds-input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
              }}
              required
            />
          </div>
          <div className="auth-actions">
            <Button
              type="submit"
              variant="primary"
              busy={reset.isPending}
              busyLabel={t("auth.resetSubmitting")}
            >
              {t("auth.resetSubmit")}
            </Button>
            <Link to="/login" className="ds-lnk">
              {t("auth.backToSignIn")}
            </Link>
          </div>
        </form>
      ) : step === "totp" ? (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            verifyTotp.mutate();
          }}
        >
          <h2 className="auth-subtitle">{t("login.totpTitle")}</h2>
          <div className="ds-field">
            <label htmlFor="totp">{t("login.totpCode")}</label>
            <input
              id="totp"
              name="totp"
              className="ds-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              required
            />
          </div>
          <div className="auth-actions">
            <Button type="submit" variant="primary" busy={verifyTotp.isPending}>
              {t("login.totpSubmit")}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <Banner kind="ok" data-testid="reset-done">
            {t("auth.resetDone")}
          </Banner>
          <div className="auth-actions">
            <Button
              variant="primary"
              onClick={() => {
                void landing().then((to) => navigate({ to }));
              }}
            >
              {t("auth.resetContinue")}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
